import { useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { notify } from "../../../../utils/notify";

import { fetchMultitenant } from "../../../../repositories/qerpapi/v3/site";
import MaskService from "../../../../utils/mask/maskService";
import { BoletoContext } from "../contexts/BoletoContext";

export default function useBilletController() {
  const {
    contractData,
    setContractData,
    setBilletData,
    empresaCnpj,
    email,
    password,
    setToken,
    token,
    setCompanyName,
    setCompanyLogoUrl,
    setButtonLoading,
    setError,
    setContracts,
    billetType,
    setBilletType,
  } = useContext(BoletoContext);

  const { push } = useHistory();

  const orderContractsDesc = ({ key, contract, otherContract }) =>
    otherContract[key] - contract[key];
  const orderContractsAsc = ({ key, contract, otherContract }) =>
    contract[key] - otherContract[key];

  const orderContractByNumber = useCallback(
    () =>
      ({ contract, otherContract, ordering = "ASC" }) =>
        ordering === "ASC"
          ? orderContractsAsc({ contract, otherContract, key: "number" })
          : orderContractsDesc({ contract, otherContract, key: "number" }),
    []
  );

  const handleChangeContract = (event) => {
    setContractAndBilletData({
      contract: event.target.value,
      billetType: billetType,
    });
  };

  const setContractAndBilletData = ({ contract, billetType }) => {
    setContractData(contract);
    setBilletData(contract[billetType] ?? []);
  };

  const fetchBilletData = useCallback(async () => {
    const res = await fetchMultitenant({
      path: `empresa/${empresaCnpj}/integration/adm/my-contracts`,
      token,
    });

    if (!res?.success) {
      notify(`Algo deu errado - ${res?.message}`, true, "error");
      setError(true);
      return;
    }

    const contracts = res?.data;

    if (!contracts?.length) {
      notify("Nenhum contrato encontrado", true, "error");
      setError(true);
      return;
    }

    contracts.sort((firstOrderParam, secondOrderParam) =>
      orderContractByNumber({
        contract: firstOrderParam.contract,
        otherContract: secondOrderParam.contract,
        ordering: "DESC",
      })
    );
    const lastContract = contracts[0];
    const billets = lastContract[billetType];
    setContracts(contracts);
    setContractData(lastContract);

    if (billets?.length) {
      const filteredBillets = billets?.filter(
        (billet) => billet.status !== "Em Carteira"
      );
      setBilletData(filteredBillets);
    }
  }, [
    billetType,
    empresaCnpj,
    orderContractByNumber,
    setBilletData,
    setContractData,
    setContracts,
    setError,
    token,
  ]);

  const selectBilletType = (_, newBilletType) => {
    setBilletType(newBilletType);
    setContractAndBilletData({
      contract: contractData,
      billetType: newBilletType,
    });
  };

  const getBilletValue = (value) =>
    value
      ? `R$ ${MaskService.toMask("decimal", parseFloat(value).toFixed(2))}`
      : "-";

  const getComapanyData = useCallback(async () => {
    const data = await fetchMultitenant({
      path: `empresa/${empresaCnpj}`,
      token,
    });

    if (!data?.success) {
      notify("Algo deu errado", true, "error");
      return;
    }

    setCompanyName(data.data?.nomeempresa ?? "");
    setCompanyLogoUrl(data.data?.logo_url ?? "");
  }, [empresaCnpj, setCompanyLogoUrl, setCompanyName, token]);

  const signIn = async () => {
    setButtonLoading(true);
    const res = await fetchMultitenant({
      path: "dmz/connection/login",
      body: JSON.stringify({
        empresaCnpj: empresaCnpj ?? "",
        email: email ?? "",
        password: password ?? "",
      }),
      method: "POST",
    });
    setButtonLoading(false);

    if (!res?.success) {
      notify(res?.message ?? "E-mail ou senha incorretos", true, "warning");
      return;
    }

    setToken(res.data);
    push("/site/boleto/listagem");
  };

  return {
    fetchBilletData,
    getBilletValue,
    getComapanyData,
    signIn,
    handleChangeContract,
    selectBilletType,
  };
}
